// src/auditoria/components/ds/DsSearchField.tsx
//
// Implementa as classes do canvas `.search` (46 artboards, `size="md"`) e
// `.searchbox` (2 artboards, `size="sm"`) — DESIGN-SYSTEM.md §4.2.
//
// `.search{display:flex;align-items:center;gap:8px;border:1px solid var(--divider);
//  border-radius:8px;padding:8px 12px;font-size:13px;color:var(--text-disabled);
//  flex:1;min-width:0;background:var(--bg-paper)}` → altura 8 + 13·1.5 + 8 ≈ 37px.
// `.searchbox` é o mesmo casco com `min-width:220px;font-size:12px;
//  color:var(--text-secondary)` e lupa de 15px.
//
// É `InputBase` dentro de um `Box` com a borda, e não `TextField size="small"`:
// o `OutlinedInput` traria notch de label, 14px e 40px de altura, e o canvas não
// tem label flutuante em lugar nenhum.
//
// ----------------------------------------------------------------------
// RODADA DE CONSERTO DA CAMADA COMPARTILHADA — DECISOES-PENDENTES.md §3.10
// Pedido por 4 LOTES (L07, L09, L19, L23).
//
// O tipo era `{ value, onChange, placeholder, size, sx }` e nada mais. Conferido:
//   - Sem `onSubmit`/`onKeyDown`. Onde a busca dispara no Enter (dte-protocolos,
//     visita fiscal) a saída foi embrulhar o campo num `<form onSubmit>` — e com dois
//     campos de busca na tela viram dois `<form>` irmãos dentro do card (L07, L19).
//     Agora `onSubmit` é chamado no Enter, sem `<form>` nenhum; `onKeyDown` fica
//     disponível para quem precisa de outra tecla (Escape, setas de sugestão).
//   - Sem `onClear`. As telas que tinham X de limpar perderam a função (L09, L23).
//     Agora `onClear` desenha o X à direita, só quando há texto.
// Somado a isso: `disabled`, `autoFocus`, `inputRef` e `ariaLabel`. Tudo ADITIVO —
// nenhuma chamada existente muda de comportamento.
//
// Divergência declarada: o X de limpar é INFERÊNCIA, não medição. Nenhum dos 46
// artboards com `.search` desenha campo preenchido com afordância de limpar. A
// gramática usada é a do próprio kit — `DsIcon name="fechar"` de 15px em
// `text.disabled`, dentro de um `ButtonBase` sem caixa, para não introduzir um
// botão-ícone redondo (proibido por §6.2).
//
// Divergência do canvas registrada e descartada: `arrecadacao` e
// `arrecadacao-modal-fechado` usam `border-color:var(--grey-400)` e `height:40px`.
// Vale a forma majoritária — `divider` e sem altura fixa.

import type { Ref, KeyboardEvent } from 'react';
import type { Theme, SxProps } from '@mui/material/styles';

import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import ButtonBase from '@mui/material/ButtonBase';

import { DsIcon } from './DsIcon';
import { DS_BOX_RADIUS } from './tokens';

// ----------------------------------------------------------------------

export type DsSearchFieldProps = {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  size?: 'md' | 'sm';
  /**
   * Chamado no Enter. Existe para a tela não precisar de `<form>` só por causa
   * disto — é o contorno que L07 e L19 tiveram de escrever.
   */
  onSubmit?: (valorAtual: string) => void;
  /** Quando presente, desenha o X de limpar à direita — só com texto no campo. */
  onClear?: () => void;
  /**
   * Repassado ao `input`. Roda ANTES do `onSubmit`: chamar `preventDefault` aqui
   * cancela o disparo no Enter.
   */
  onKeyDown?: (evento: KeyboardEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  /** Nome acessível do campo. Padrão: o próprio `placeholder`. */
  ariaLabel?: string;
  sx?: SxProps<Theme>;
};

export function DsSearchField({
  value,
  onChange,
  placeholder = 'Buscar…',
  size = 'md',
  onSubmit,
  onClear,
  onKeyDown,
  disabled,
  autoFocus,
  inputRef,
  ariaLabel,
  sx,
}: DsSearchFieldProps) {
  const ehCompacto = size === 'sm';

  // A cor da raiz é a do texto de apoio do canvas: a lupa herda dela, e o
  // placeholder repete o mesmo token. Só o texto digitado é `text.primary`.
  const corDeApoio = ehCompacto ? 'text.secondary' : 'text.disabled';
  const mostrarLimpar = Boolean(onClear) && value.length > 0 && !disabled;

  const aoTeclar = (evento: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(evento);

    if (evento.key === 'Enter' && !evento.defaultPrevented && onSubmit) {
      // `preventDefault` SÓ quando o próprio campo trata o Enter. Sem essa
      // condição a tecla parava de submeter o `<form>` que algumas telas põem
      // em volta do campo — o contorno que elas criaram justamente por falta de
      // `onSubmit` aqui (ver DECISOES-PENDENTES §3.10).
      evento.preventDefault();
      onSubmit(value);
    }
  };

  return (
    <Box
      sx={[
        {
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: `${DS_BOX_RADIUS}px`,
          padding: '8px 12px',
          backgroundColor: 'background.paper',
          color: corDeApoio,
          ...(ehCompacto
            ? { fontSize: 12, minWidth: 220 }
            : { fontSize: 13, flex: 1, minWidth: 0 }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <DsIcon name="buscar" size={ehCompacto ? 15 : 18} sx={{ flex: '0 0 auto' }} />

      <InputBase
        value={value}
        onChange={(evento) => onChange(evento.target.value)}
        onKeyDown={aoTeclar}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        inputRef={inputRef}
        inputProps={{ 'aria-label': ariaLabel ?? placeholder }}
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: 'inherit',
          '& .MuiInputBase-input': {
            padding: 0,
            height: 'auto',
            fontSize: 'inherit',
            lineHeight: 1.5,
            color: 'text.primary',
            '&::placeholder': { color: corDeApoio, opacity: 1 },
          },
        }}
      />

      {mostrarLimpar && (
        <ButtonBase
          type="button"
          aria-label="Limpar busca"
          onClick={() => onClear?.()}
          sx={{
            flex: '0 0 auto',
            display: 'inline-grid',
            placeItems: 'center',
            width: 18,
            height: 18,
            borderRadius: '50%',
            color: corDeApoio,
            '&:hover': { color: 'text.primary' },
          }}
        >
          <DsIcon name="fechar" size={15} />
        </ButtonBase>
      )}
    </Box>
  );
}
